import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { FormField } from "@/components/common/FormField";
import { cnInput } from "@/components/common/formClasses";
import {
  ServiceCategory,
  SERVICE_CATEGORY_LABEL,
  type ServiceCategoryType,
} from "@/features/permission";
import { useSystemRoleDetail, useUpsertSystemRole } from "../hooks/useSystemRoles";
import { useSystemRoleStore } from "../stores/systemRoleStore";

const CATEGORIES: ServiceCategoryType[] = [
  ServiceCategory.DISPATCH,
  ServiceCategory.FREIGHT_OWNER,
  ServiceCategory.PARTNER,
  ServiceCategory.ADMIN,
];

const schema = z.object({
  code: z.string().min(1, "code 는 필수입니다"),
  label: z.string().min(1, "label 은 필수입니다"),
  description: z.string().optional(),
  serviceCategory: z.enum([
    ServiceCategory.DISPATCH,
    ServiceCategory.FREIGHT_OWNER,
    ServiceCategory.PARTNER,
    ServiceCategory.ADMIN,
  ]),
  isDefault: z.boolean(),
  isActive: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

interface SystemRoleFormProps {
  roleGuid: string | null;
  onSavedToEdit: (guid: string) => void;
}

export function SystemRoleForm({ roleGuid, onSavedToEdit }: SystemRoleFormProps) {
  const isEdit = !!roleGuid;
  const { data: detail, isLoading } = useSystemRoleDetail(roleGuid);
  const upsert = useUpsertSystemRole();
  const openDelete = useSystemRoleStore((s) => s.openDeleteFor);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: "",
      label: "",
      description: "",
      serviceCategory: ServiceCategory.DISPATCH,
      isDefault: false,
      isActive: true,
    },
  });

  const isDefault = watch("isDefault");
  const isActive = watch("isActive");

  // Drawer keys this component on roleGuid, so add-mode mounts fresh with defaultValues.
  // Only need to populate edit-mode form once the detail query resolves.
  useEffect(() => {
    if (detail) {
      reset({
        code: detail.code,
        label: detail.label,
        description: detail.description ?? "",
        serviceCategory: detail.serviceCategory,
        isDefault: detail.isDefault,
        isActive: detail.isActive,
      });
    }
  }, [detail, reset]);

  const onSubmit = (values: FormValues) => {
    upsert.mutate(
      {
        roleGuid: roleGuid ?? undefined,
        code: values.code.trim(),
        label: values.label.trim(),
        description: values.description?.trim() || undefined,
        serviceCategory: values.serviceCategory,
        isDefault: values.isDefault,
        isActive: values.isActive,
      },
      {
        onSuccess: (saved) => {
          if (!isEdit && saved?.guid) {
            onSavedToEdit(saved.guid);
          }
        },
      },
    );
  };

  if (isEdit && isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center text-xs text-gray-400">
        로딩 중...
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-1 flex-col overflow-hidden"
    >
      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
        <FormField
          label="code"
          required
          error={errors.code?.message}
          hint={isEdit ? "code 는 변경할 수 없습니다." : undefined}
        >
          <input
            {...register("code")}
            disabled={isEdit}
            placeholder="dispatch-admin"
            className={cnInput("font-mono disabled:bg-gray-100 disabled:text-gray-500")}
          />
        </FormField>

        <FormField label="label" required error={errors.label?.message}>
          <input
            {...register("label")}
            placeholder="배차사 관리자"
            className={cnInput()}
          />
        </FormField>

        <FormField label="description">
          <textarea
            {...register("description")}
            rows={2}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-xs outline-none focus:border-blue-500"
          />
        </FormField>

        <FormField
          label="serviceCategory"
          required
          hint={isEdit ? "serviceCategory 는 변경할 수 없습니다." : undefined}
        >
          <select
            {...register("serviceCategory")}
            disabled={isEdit}
            className={cnInput("disabled:bg-gray-100 disabled:text-gray-500")}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {SERVICE_CATEGORY_LABEL[c]} ({c})
              </option>
            ))}
          </select>
        </FormField>

        <div className="space-y-2 border-t border-gray-200 pt-3">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-xs font-medium text-gray-700">
                기본 역할 (isDefault)
              </label>
              <p className="text-xs text-gray-500">
                회사 가입 시 모든 직원에게 자동 부여
              </p>
            </div>
            <Switch
              checked={isDefault}
              onCheckedChange={(v) => setValue("isDefault", v)}
              size="sm"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <label className="text-xs font-medium text-gray-700">
                활성 (isActive)
              </label>
              <p className="text-xs text-gray-500">
                비활성 시 JWT 권한 계산에서 제외
              </p>
            </div>
            <Switch
              checked={isActive}
              onCheckedChange={(v) => setValue("isActive", v)}
              size="sm"
            />
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3">
        {isEdit && roleGuid ? (
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => openDelete(roleGuid)}
            className="text-xs"
          >
            삭제
          </Button>
        ) : (
          <span />
        )}
        <Button
          type="submit"
          disabled={upsert.isPending}
          className="bg-blue-600 px-4 text-xs font-bold hover:bg-blue-700"
        >
          {upsert.isPending ? "저장 중..." : isEdit ? "저장" : "다음 (권한 매핑)"}
        </Button>
      </div>
    </form>
  );
}
